import { useMemo, useState, type VoidFunctionComponent } from 'react';
import { Modal } from 'react-bootstrap';

import { useAddToast } from '../../../../../../../contexts/ToastContext';
import useCreateTeamGroupTeams from '../../../../../../../hooks/api/teams/teamGroups/useCreateTeamGroupTeams';
import useTeams from '../../../../../../../hooks/api/teams/useTeams';
import { getErrorMessage } from '../../../../../../../ReactQuery/errors';
import { selectOptionFactory } from '../../../../../../../utils';
import Button, { ButtonGroup } from '../../../../../../generic/Button';
import Loader from '../../../../../../generic/Loader';
import Select from '../../../../../../generic/Select/Select';
import type { TeamGroupTeamData } from '../../../../../../../../shared/types/routes/teamGroups';
import type { SelectOption } from '../../../../../../../types';

type Props = {
  isOpen: boolean;
  onHide: () => void;
  teamGroupId: number;
  teamGroupName: string;
  teamGroupTeams: TeamGroupTeamData[];
};

/**
 * Modal for adding teams to a team group.
 *
 * @param {Props} props - The props for the component.
 * @returns {React.ReactNode} - The component.
 */
const AddTeamModal: VoidFunctionComponent<Props> = ({
  isOpen,
  onHide,
  teamGroupId,
  teamGroupName,
  teamGroupTeams,
}) => {
  const addToast = useAddToast();
  const { data: teams, isLoading: isLoadingTeams } = useTeams();
  const [selectedOptions, setSelectedOptions] = useState<Array<SelectOption<number>>>([]);

  const selectedOptionsSet = useMemo(
    () => new Set(selectedOptions.map(option => option.value)),
    [selectedOptions],
  );

  const teamGroupTeamsSet = useMemo(
    () => new Set(teamGroupTeams.map(team => team.id)),
    [teamGroupTeams],
  );

  const addableTeams = useMemo(
    () => {
      return teams
        ?.filter(team => !selectedOptionsSet.has(team.id) && !teamGroupTeamsSet.has(team.id))
        ?.map((team) => selectOptionFactory(team.id, team.name)) ?? [];
    },
    [teams, selectedOptionsSet, teamGroupTeamsSet],
  );

  const { mutateAsync: addTeamGroupTeams } = useCreateTeamGroupTeams(
    { teamIds: selectedOptions.map(option => option.value) },
    teamGroupId,
  );

  //  Handle the save button click.
  const handleSave = async () => {
    try {
      await addTeamGroupTeams();
      addToast({
        type: 'success',
        content: `The team group "${teamGroupName}" has been updated successfully.`,
      });
      setSelectedOptions([]);
      onHide();
    } catch (error) {
      addToast({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  };

  return (
    <Modal show={isOpen} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          Add teams to {teamGroupName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoadingTeams
          ? (
            <Loader style={{ marginTop: '18px', marginBottom: '18px' }} />
            ) : (
              <Select
                isMulti
                id="teamGroupAddTeamsSelect"
                options={addableTeams}
                onChange={setSelectedOptions}
                value={selectedOptions}
              />
            )}
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type="secondary"
            size="medium"
            onClick={onHide}
            isDisabled={isLoadingTeams}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="medium"
            onClick={handleSave}
            isDisabled={isLoadingTeams || !selectedOptions.length}
          >
            Add
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default AddTeamModal;
